import React, { ComponentProps, FC, useState } from 'react';
import { MessageDialog, QuestionDialog } from 'src/components/widgets/dialog';
import { T, stringFormat } from 'src/shared/helpers';
import ConnectModelDialog from './connectModelDialog';

type CatalogProps = ComponentProps<typeof ConnectModelDialog>;
type LinkedStoreCode = CatalogProps['additionalCodes'][number];
type ConversionProduct = { name: string; sku: string; thumbnail: string | null };

type Props = Omit<CatalogProps, 'onConfirm'> & {
    linkedStoreCodes: LinkedStoreCode[];
    convert?: boolean;
    onConfirm: (product: ConversionProduct | null, salesChannelID: string) => void;
};

const ConvertStandaloneProductDialog: FC<Props> = ({ linkedStoreCodes, convert = true, onConfirm, ...catalogProps }) => {
    const connectedCodes = linkedStoreCodes.filter((code) => catalogProps.salesChannels.some((channel) => channel.id === code.salesChannelID));
    const [selectedCode, setSelectedCode] = useState<LinkedStoreCode | undefined>(connectedCodes.length === 1 ? connectedCodes[0] : undefined);
    const storeName = (code: LinkedStoreCode) => {
        const channel = catalogProps.salesChannels.find((channel) => channel.id === code.salesChannelID);
        return channel?.detail?.properties?.name || channel?.ecommerceSettings?.remoteUrl || T._('Online store', 'Admin');
    };

    if (!convert || linkedStoreCodes.length === 0) {
        return <ConnectModelDialog {...catalogProps} onConfirm={(product, salesChannelID) => onConfirm(product, salesChannelID)} />;
    }

    if (connectedCodes.length === 0) {
        return (
            <MessageDialog
                title={T._('Convert to a store product', 'Admin')}
                message={T._('The stores linked to this product are no longer connected. Reconnect a store before converting this product.', 'Admin')}
            />
        );
    }

    if (!selectedCode) {
        return (
            <MessageDialog
                title={T._('Choose the online store', 'Admin')}
                message={T._('Choose which linked store will become the primary store for this product.', 'Admin')}
                buttons={connectedCodes.map((code) => ({
                    label: `${storeName(code)} (${code.code})`,
                    onClick: () => setSelectedCode(code)
                }))}
            />
        );
    }

    return (
        <QuestionDialog
            title={T._('Convert to a store product', 'Admin')}
            message={stringFormat(
                T._(
                    'This standalone product will become a product of {0} and will be published using the existing store product ({1}) and variant mappings. Other store links will be kept.',
                    'Admin'
                ),
                storeName(selectedCode),
                selectedCode.code
            )}
            buttonYesLabel={T._('Convert and publish', 'Admin')}
            buttonNoLabel={T._('Cancel', 'Admin')}
            onYesClick={() => onConfirm({ name: selectedCode.merchantProductName, sku: selectedCode.code, thumbnail: null }, selectedCode.salesChannelID)}
        />
    );
};

export default ConvertStandaloneProductDialog;
